/**
 * Plugin de runtime do CKEditor que transforma cada variável do template em um
 * **widget inline atômico**.
 *
 * Por que um widget e não o chip do plugin `mention`: o `mention` é um atributo
 * de texto, então o conteúdo do chip é editável. Apagar uma letra dentro dele
 * faz o post-fixer do próprio `mention` descartar o atributo, e como o
 * Handlebars vive no atributo, a variável se perdia. Como widget:
 *
 * - não dá para digitar/apagar dentro dele — só selecionar e remover inteiro;
 * - é um nó único do modelo, então operações de tabela (inserir/remover linha
 *   ou coluna, merge, cortar e colar a célula) o levam junto.
 *
 * Registrado via `config.extraPlugins`, o que roda **antes** do carregamento do
 * conteúdo — se fosse no evento `ready`, o upcast não existiria ainda e os
 * spans do template seriam descartados na entrada.
 *
 * Não importa nada de `@ckeditor/*` de propósito: o bundle em
 * `core/libs/ckeditor5/build` traz a própria cópia do `ckeditor5-core`, e um
 * segundo import quebraria com `ckeditor-duplicated-modules`. Tudo aqui usa só
 * a API pública da instância do editor.
 */

export const TEMPLATE_VARIABLE_ELEMENT = 'templateVariable';
export const TEMPLATE_VARIABLE_DATA_ATTRIBUTE = 'data-cred-var';
export const TEMPLATE_VARIABLE_CLASS = 'cred-var';

/**
 * @param labelFor Traduz a expressão Handlebars (`{{Amount}}`) no texto exibido
 * ("Valor"). Injetado para o plugin não depender do Angular.
 */
export function createTemplateVariablePlugin(
  labelFor: (expression: string) => string
): any {
  return class TemplateVariablePlugin {
    editor: any;

    constructor(editor: any) {
      this.editor = editor;
    }

    init(): void {
      this.defineSchema();
      this.defineConverters();
      this.convertMentionsToWidgets();
    }

    private defineSchema(): void {
      // `$inlineObject` já traz isInline + isObject + allowWhere: '$text'.
      this.editor.model.schema.register(TEMPLATE_VARIABLE_ELEMENT, {
        inheritAllFrom: '$inlineObject',
        allowAttributes: ['expression', 'label']
      });
    }

    private defineConverters(): void {
      const conversion = this.editor.conversion;

      conversion.for('upcast').elementToElement({
        view: {
          name: 'span',
          attributes: { [TEMPLATE_VARIABLE_DATA_ATTRIBUTE]: true }
        },
        model: (viewElement: any, { writer }: any) => {
          const expression =
            viewElement.getAttribute(TEMPLATE_VARIABLE_DATA_ATTRIBUTE) ?? '';

          return writer.createElement(TEMPLATE_VARIABLE_ELEMENT, {
            expression,
            label: this.readLabel(viewElement) || labelFor(expression)
          });
        }
      });

      // Pipeline de dados: é isto que vai para o FormControl e para a API.
      conversion.for('dataDowncast').elementToElement({
        model: TEMPLATE_VARIABLE_ELEMENT,
        view: (modelElement: any, { writer }: any) =>
          this.buildSpan(writer, modelElement, false)
      });

      // Pipeline de edição: o mesmo span, marcado como widget.
      conversion.for('editingDowncast').elementToElement({
        model: TEMPLATE_VARIABLE_ELEMENT,
        view: (modelElement: any, { writer }: any) =>
          this.buildSpan(writer, modelElement, true)
      });
    }

    private buildSpan(
      writer: any,
      modelElement: any,
      isEditing: boolean
    ): any {
      const expression = modelElement.getAttribute('expression') ?? '';
      const label = modelElement.getAttribute('label') || labelFor(expression);

      const attributes: Record<string, string> = {
        class: isEditing
          ? `${TEMPLATE_VARIABLE_CLASS} ck-widget`
          : TEMPLATE_VARIABLE_CLASS,
        [TEMPLATE_VARIABLE_DATA_ATTRIBUTE]: expression
      };

      if (isEditing) {
        attributes['contenteditable'] = 'false';
      }

      const span = writer.createRawElement(
        'span',
        attributes,
        (domElement: HTMLElement) => {
          domElement.textContent = label;
        }
      );

      if (isEditing) {
        // É o que faz o plugin Widget (já presente no bundle, via Table)
        // tratar o span como unidade: seleção, delete e clique.
        writer.setCustomProperty('widget', true, span);
        // Lista, não função: o Widget do bundle (v37) faz `push`/`reduce` nela
        // ao selecionar o widget — com função, clicar no chip lançava
        // `e.reduce is not a function`.
        writer.setCustomProperty('widgetLabel', [label], span);
      }

      return span;
    }

    private readLabel(viewElement: any): string {
      const child = viewElement.getChild?.(0);

      return child?.data ?? '';
    }

    /**
     * O dropdown de `{` continua sendo o do plugin `mention`, que insere um
     * texto com o atributo `mention`. Este post-fixer troca esse texto pelo
     * widget no mesmo ciclo de alteração, então o estado editável é transitório.
     *
     * De brinde, converte também os chips `span.mention` de templates salvos
     * antes desta mudança, que o `mention` faz upcast normalmente.
     */
    private convertMentionsToWidgets(): void {
      const model = this.editor.model;

      model.document.registerPostFixer((writer: any) => {
        const pending: { expression: string; start: any; end: any }[] = [];

        for (const root of model.document.getRootNames()) {
          const range = model.createRangeIn(model.document.getRoot(root));

          for (const item of range.getItems()) {
            if (!item.is?.('$textProxy') || !item.hasAttribute?.('mention')) {
              continue;
            }

            const mention = item.getAttribute('mention');
            const expression = mention?.id ?? '';

            if (!expression) continue;

            const start = model.createPositionAt(item.parent, item.startOffset);

            pending.push({
              expression,
              start,
              end: start.getShiftedBy(item.data.length)
            });
          }
        }

        if (pending.length === 0) return false;

        // De trás para frente: remover um trecho invalida os offsets seguintes.
        pending.reverse().forEach(({ expression, start, end }) => {
          writer.remove(writer.createRange(start, end));
          writer.insertElement(
            TEMPLATE_VARIABLE_ELEMENT,
            { expression, label: labelFor(expression) },
            start
          );
        });

        return true;
      });
    }
  };
}
